// src/pages/admin/BannerCreatePage.jsx

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, setDoc } from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { db } from '../../firebase';

export default function BannerCreatePage() {
  const navigate = useNavigate();
  const bannersColl = collection(db, 'banners');
  const newBannerRef = useMemo(() => doc(bannersColl), [bannersColl]);
  const bannerId = newBannerRef.id;

  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [tag, setTag] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [linkId, setLinkId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ==============================
  // convertToWebp: PNG/JPEG 등 이미지를 WebP Blob으로 변환
  // ==============================
  const convertToWebp = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('WebP 변환 실패'));
              }
            },
            'image/webp',
            1.0
          );
        };
        img.onerror = () => reject(new Error('이미지 로딩 실패'));
        img.src = reader.result;
      };
      reader.onerror = () => reject(new Error('FileReader 실패'));
      reader.readAsDataURL(file);
    });
  };

  // ==============================
  // uploadFileWithCache: Blob/File 객체 업로드 + Cache-Control 설정
  // ==============================
  const uploadFileWithCache = async (blobOrFile, folderPath, filename, contentType) => {
    const storage = getStorage();
    const fileRef = storageRef(storage, `${folderPath}/${filename}`);
    const metadata = {
      contentType,
      cacheControl: 'public, max-age=43200' // 12시간
    };
    await uploadBytes(fileRef, blobOrFile, metadata);
    const url = await getDownloadURL(fileRef);
    return url;
  };

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleCancel = () => {
    navigate('/admin', { state: { tab: 'banners' } });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      let imagePath = '';
      if (bannerFile) {
        if (bannerFile.type.startsWith('image/')) {
          // 이미지인 경우 WebP로 변환 후 업로드
          const webpBlob = await convertToWebp(bannerFile);
          const webpFilename = `${bannerId}_banner`;
          imagePath = await uploadFileWithCache(
            webpBlob,
            `banners/${bannerId}`,
            webpFilename,
            'image/webp'
          );
        } else {
          // 이미지가 아니면 그대로 업로드
          const filename = `${bannerId}_banner`;
          imagePath = await uploadFileWithCache(
            bannerFile,
            `banners/${bannerId}`,
            filename,
            bannerFile.type
          );
        }
      }

      await setDoc(newBannerRef, {
        id: bannerId,
        tag,
        title,
        subtitle,
        linkId,
        bannerPath: imagePath
      });

      alert('배너 등록 완료!');
      navigate('/admin', { state: { tab: 'banners' } });
    } catch (err) {
      console.error(err);
      alert('등록 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] text-gray-100 py-10">
      <div className="max-w-7xl mx-auto px-4 space-y-12">
        <h1 className="text-3xl font-bold text-center">메인 배너 등록</h1>
        <div className="bg-[#1a1a1a] rounded-2xl overflow-hidden divide-x divide-neutral-700">
          <div className="flex flex-col lg:flex-row">
            {/* Preview Panel */}
            <div className="flex-none px-12 py-16 flex justify-center">
              <div className="relative rounded-2xl shadow-lg overflow-hidden bg-neutral-800 flex items-end w-[480px] h-[270px] transition-all duration-200">
                {bannerPreview ? (
                  <img
                    src={bannerPreview}
                    alt="배너 미리보기"
                    className="absolute inset-0 w-full h-full object-cover opacity-70"
                    draggable={false}
                  />
                ) : (
                  <div className="absolute inset-0 bg-neutral-800 flex items-center justify-center">
                    <span className="text-gray-500">배너 없음</span>
                  </div>
                )}
                <div className="relative z-10 p-8 text-left text-white w-full">
                  {tag && (
                    <div className="mb-2 text-xs bg-black/50 rounded-full px-3 py-1 inline-block font-semibold">
                      {tag}
                    </div>
                  )}
                  <div className="font-bold text-2xl md:text-3xl drop-shadow-sm mb-2 whitespace-pre-wrap">
                    {title || '제목 없음'}
                  </div>
                  <div className="text-sm opacity-80 whitespace-pre-wrap">
                    {subtitle || '부제목 없음'}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Panel */}
            <div className="flex-1 p-12">
              <form className="space-y-6" onSubmit={handleSubmit}>
                {/* 배너 이미지 */}
                <div className="flex items-center mb-4">
                  <label className="w-32 font-medium">배너 이미지</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={submitting}
                    className="w-full bg-neutral-700 border border-neutral-500 px-3 py-2 rounded text-white"
                  />
                </div>

                {/* 태그 */}
                <div className="flex items-center mb-4">
                  <label className="w-32 font-medium">태그</label>
                  <input
                    type="text"
                    value={tag}
                    onChange={e => setTag(e.target.value)}
                    disabled={submitting}
                    placeholder="ex) 게임, 이벤트"
                    className="w-full bg-neutral-700 border border-neutral-500 px-3 py-2 rounded text-white"
                  />
                </div>

                {/* 제목 */}
                <div className="flex items-center mb-4">
                  <label className="w-32 font-medium">제목</label>
                  <textarea
                    rows={2}
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    disabled={submitting}
                    placeholder="제목을 입력하세요"
                    className="w-full bg-neutral-700 border border-neutral-500 px-3 py-2 rounded text-white resize-none"
                  />
                </div>

                {/* 부제목 */}
                <div className="flex items-center mb-4">
                  <label className="w-32 font-medium">부제목</label>
                  <textarea
                    rows={2}
                    value={subtitle}
                    onChange={e => setSubtitle(e.target.value)}
                    disabled={submitting}
                    placeholder="2줄 이내로 입력"
                    className="w-full bg-neutral-700 border border-neutral-500 px-3 py-2 rounded text-white resize-none"
                  />
                </div>

                {/* 링크 ID */}
                <div className="flex items-center mb-4">
                  <label className="w-32 font-medium">연결 링크</label>
                  <input
                    type="text"
                    value={linkId}
                    onChange={e => setLinkId(e.target.value)}
                    disabled={submitting}
                    placeholder="연결할 링크의 고유 ID"
                    className="w-full bg-neutral-700 border border-neutral-500 px-3 py-2 rounded text-white"
                  />
                </div>

                {/* 버튼 그룹 */}
                <div className="flex justify-end gap-4 pt-4 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={submitting}
                    className="bg-gray-600 text-gray-200 px-6 py-2 rounded hover:bg-gray-500 disabled:opacity-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-500"
                  >
                    {submitting ? '등록중…' : '등록'}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
